/**
 * xt-tracker SDK 入口文件
 *
 * Web 埋点 SDK，支持四层参数结构：
 * - Layer 1: 全局公共参数 (common)
 * - Layer 2: 事件参数 (event)
 * - Layer 3: 业务参数 (business)
 * - Layer 4: 独立采集 & 灵活组装
 */

// 导出 Tracker 类
export { Tracker } from './core/tracker'

// 导出配置相关
export { DEFAULT_CONFIG, mergeConfig, validateConfig } from './core/config'
export { DATA_ATTRS } from './core/constants'

// 导出所有类型
export type {
  // Common 层
  CommonParams,
  PartialCommonParams,
  // Event 层
  EventType,
  EventBase,
  EventData,
  PageParams,
  PageEventParams,
  ClickEventParams,
  AfterClickEventParams,
  AfterClickInterceptStatus,
  ExposeEventParams,
  CustomEventParams,
  CustomInputParams,
  CustomFilterParams,
  CustomSortParams,
  CustomFileParams,
  CustomPaginationParams,
  CustomDragParams,
  CustomSubmitParams,
  CustomRequestParams,
  ErrorEventParams,
  BusinessParams,
  // Config
  RouteInfo,
  RoutePathResolver,
  ReferrerResolver,
  TrackerConfig,
  RequiredTrackerConfig,
  ResolvedTrackerConfig,
  // Network Monitor
  RequestMethod,
  RequestStatus,
  RequestType,
  RequestData,
  PageRequestsStore,
  RequestFilter,
  NetworkModuleConfig,
  PendingRequestData,
  ResponseInfo,
  // 组合数据结构
  TrackData,
  // Storage
  IStorage,
  // Dynamic Config
  DynamicConfig,
  DynamicPageConfig,
  DynamicElementConfig,
  DynamicEventConfig,
  DynamicFieldConfig,
  FieldSource,
  CachedDynamicConfig,
} from './types'

// 导出采集器（供高级用户独立使用）
export { collectCommon, refreshDynamicFields } from './collectors/common'
export { collectPageEvent, collectPageLeaveEvent, resolveRoutePath, resolveReferrerPage, getPageId, collectPageParams } from './collectors/page'
export { collectClickEvent, shouldTrackClick } from './collectors/click'
export {
  collectExposeEvent,
  collectExposeEventFromElement,
  type ExposeMeta,
} from './collectors/exposure'
export type { ScanMetrics } from './modules/auto-exposure-scanner'
export {
  createRequestData,
  filterRequests,
  getRequestSummary,
} from './collectors/network'

// 导出工具函数
export {
  generateTrackId,
  generateUuid,
  generateDeviceUuid,
  getOrCreateSessionId,
} from './utils/uuid'
export {
  parseUserAgent,
  getScreenOrientation,
  getOsTheme,
  getNetworkType,
  getOsLanguage,
  getOsRegion,
  getTimeZone,
  getOsFontSize,
  type UAParseResult,
} from './utils/ua-parser'
export {
  camelToSnakeCase,
  toSnakeCaseKeys,
  serializeTrackData,
} from './utils/transform'
export {
  LocalStorageAdapter,
  SessionStorageAdapter,
  localStorageAdapter,
  sessionStorageAdapter,
} from './utils/storage'
export {
  shouldMonitorUrl,
  generateRequestId,
  type PendingRequest,
  type ResponseInfo as InterceptorResponseInfo,
  type InterceptorCallbacks,
  type InterceptorConfig,
} from './utils/request-interceptor'

// 导出 Reporter（供高级用户使用）
export { Reporter, type ReporterConfig } from './reporter'

// 导出动态配置相关
export { DynamicConfigManager } from './core/dynamic-config'
export { MOCK_DYNAMIC_CONFIG } from './core/mock-dynamic-config'
export { matchRoutePath, findMatchingPageId } from './utils/route-matcher'

// 导出 AfterClick 相关
export { AfterClickModule, type AfterClickModuleConfig } from './modules/after-click'
export {
  activateAfterClickIntercept,
  type AfterClickInterceptConfig,
  type CapturedXHRResponse,
} from './utils/after-click-interceptor'

// 创建默认单例
import { Tracker } from './core/tracker'
const tracker = new Tracker()

// 默认导出单例
export default tracker
